import { useEffect, useState } from 'react';
// @mui
import {
  Box,
  Button,
  Typography,
  InputBase,
  TextField,
  Stack,
  CardContent,
  NativeSelect,
  Card,
  Divider,
  styled,
} from '@mui/material';
// import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import moment from 'moment';

import useTable from '../../hooks/useTable';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { TableSkeleton } from '../../components/table';

import conObj from '../../utils/connection-assistant';
import CustomDateRangePicker from '../../components/CustomDateRangePicker';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';

import { fDate } from '../../utils/formatTime';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';

const _filters = {
  dateFrom: '',
  dateTo: '',
  transactionType: '',
};

const StyledCard = styled(Card)({
  borderRadius: '8px 8px 0 0',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#094221',
  '& > :last-child': {
    padding: '10px 20px',
  },
});

const StyledCardContent = styled(CardContent)({
  padding: '10px 20px',
  '&:last-child': {
    padding: '10px 20px',
  },
});

const StyledCardSmallText = styled('p')({
  margin: 0,
  padding: 0,
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '10px',
});

const StyledCardBottom = styled(Card)({
  borderRadius: '0 0 8px 8px',
  boxShadow: '#373644',
  backgroundColor: '#094221eb',
  '& > :last-child': {
    padding: '10px 20px',
  },
});

const EmptyContainer = styled(Stack)({
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#383649',
  color: 'rgba(255, 255, 255, 0.4)',
});

const CustomTextField = styled(TextField)({
  '& .MuiInputBase-input': {
    color: '#fff',
    '&::placeholder': {
      color: 'gray',
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
});

const CategoryButton = styled(Stack)(({ isSelected }) => ({
  padding: '5px 10px',
  color: '#003C79',
  border: isSelected ? '1px solid #003C79' : '1px solid #F0C335',
  backgroundColor: isSelected ? '#F0C335' : '#fff',
  cursor: 'pointer',
}));

const StyledButton = styled(Button)({
  padding: '5px 10px',
  color: 'fff',
  backgroundColor: '#4CAF50',
  cursor: 'pointer',
  '.Mui-disabled': {
    color: 'rgba(145, 158, 171, 0.8)',
    boxShadow: 'none',
    backgroundColor: 'rgba(145, 158, 171, 0.24)',
  },
  ':hover': {
    backgroundColor: '#4CAF50',
  }
});

const InputStack = styled(Stack)(({ theme }) => ({
  padding: 12,
  borderRadius: 5,
  height: 42,
  border: '1px solid #fff',
  alignItems: 'center',
  cursor: 'pointer',
  [theme.breakpoints.up('sm')]: {
    minWidth: 150,
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const InputText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: '#fff',
  flex: 1,
}));

const TransactionHistory = () => {
  const { translate } = useLocales();

  const { memberBalance } = useSelector((x) => x.lookup);

  const [tableData, setTableData] = useState([]);

  const [page, setPage] = useState(1);

  const [totalRecord, setTotalRecord] = useState(0);

  const [filters, setFilters] = useState(_filters);

  const [isLoading, setIsLoading] = useState(false);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);

  const [amountInThousandths, setAmountInThousandths] = useState(false);

  const [dateRange, setDateRange] = useState(null);

  const pageLimit = 5;

  const { dense, rowsPerPage } = useTable();

  useEffect(() => {
    if (memberBalance?.currencyCode.toUpperCase() === 'MMK' || memberBalance?.currencyCode.toUpperCase() === 'VND') {
      setAmountInThousandths(true);
    }
  }, [memberBalance]);

  useEffect(() => {
    getTransactionHistory();
  }, [page, filters]);

  useEffect(() => {
    if (dateRange) {
      if (dateRange?.start) {
        setFilters((prevState) => ({ ...prevState, dateFrom: dateRange?.start }));
      }
      if (dateRange?.end) {
        setFilters((prevState) => ({ ...prevState, dateTo: dateRange?.end }));
      }
    }
  }, [dateRange]);

  const getTransactionHistory = () => {
    setIsLoading(true);
    const requestData = {
      'named-date_from': filters?.dateFrom ? moment(filters?.dateFrom).format('YYYY-MM-DD') : '',
      'named-date_to': filters?.dateTo ? moment(filters?.dateTo).format('YYYY-MM-DD') : '',
      'named-activity_type': filters?.transactionType || '', // deposit/withdraw/bonus/rebate/adjustment
      // ...(filters?.transactionType === 'deposit' ? { 'named-is_deposit': 'deposit' } : {}),
      // ...(filters?.transactionType === 'withdraw' ? { 'named-is_withdraw': 'withdraw' } : {}),
    };

    conObj
      .post(`transactions/index.json?page=${page}&limit=${pageLimit}`, requestData)
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.status === 1) {
            setTableData(res.data?.data?.data);
            setTotalRecord(res.data?.data?.page.total_data);
          }
        }
      })
      .catch((err) => {
        console.log(`Error: ${err.message}`);
      })
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleFilterChange = (name, val) => {
    setFilters((prevState) => ({ ...prevState, [name]: val }));
  };

  const handleChangePage = (e, _page) => {
    setPage(_page);
  };

  const denseHeight = dense ? 60 : 80;

  const isNotFound = (!tableData.length && !!filters) || (!isLoading && !tableData.length);

  // TAG CONTAINER CHANGING OF COLOR DEPENDS ON THE STATUS
  const getStatusContanerColor = (status) => {
    switch (status) {
      case 'PENDING':
      case 'বিচারাধীন'.toUpperCase():
        return 'rgba(66, 165, 245, 0.1)';
      case 'APPROVED':
      case 'অনুমোদিত'.toUpperCase():
        return 'rgba(0, 255, 0, 0.1)';
      case 'CANCELLED':
        return 'rgba(255, 255, 255, 0.08)';
      case 'REJECTED':
      case 'প্রত্যাখ্যাত'.toUpperCase():
        return 'rgba(255, 36, 0, 0.1)';
      default:
        return '#242F39';
    }
  };

  const StyledCardTag = styled('div')(({ status }) => ({
    borderRadius: '4px',
    backgroundColor: getStatusContanerColor(status.toUpperCase()),
    fontSize: '3px',
    padding: '2px 5px',
    color: getStatusLabelColor(status.toUpperCase()),
    width: 'fit-content',
  }));

  // TAG LABEL CHANGING OF COLOR DEPENDS ON THE STATUS
  const getStatusLabelColor = (status) => {
    switch (status) {
      case 'PENDING':
      case 'বিচারাধীন'.toUpperCase():
        return '#42a5f5';
      case 'APPROVED':
      case 'অনুমোদিত'.toUpperCase():
        return '#00FF00';
      case 'CANCELLED':
        return 'rgba(255, 255, 255, 0.7)';
      case 'REJECTED':
      case 'প্রত্যাখ্যাত'.toUpperCase():
        return '#ff2400';
      default:
        return '#242F39';
    }
  };

  return (
    <Page title="Transaction History" sx={{ background: '#000', p: '1rem' }}>
      <Box component={Stack} spacing={2}>
        <Box sx={{ width: '100%' }}>
          <Stack
            direction={{ xs: 'column' }}
            spacing={1}
            sx={{ margin: '0 0 2px 0', p: { xs: 1 }, backgroundColor: '#094221' }}
          >
            {/* <Stack direction={'row'} spacing={2} alignItems={'center'} mt={1}>
              <CategoryButton
                flex={1}
                alignItems={'center'}
                isSelected={filters.transactionType === 'deposit'}
                onClick={() => handleFilterChange('transactionType', 'deposit')}
              >
                {translate('deposit')}
              </CategoryButton>
              <CategoryButton
                flex={1}
                alignItems={'center'}
                isSelected={filters.transactionType === 'withdraw'}
                onClick={() => handleFilterChange('transactionType', 'withdraw')}
              >
                {translate('withdraw')}
              </CategoryButton>
            </Stack> */}

            <Stack direction="row" spacing={1}>
              <TextField
                select
                variant="outlined"
                value={filters?.transactionType}
                SelectProps={{ native: true }}
                onChange={(e) => setFilters((prevState) => ({ ...prevState, transactionType: e.target.value }))}
                size="small"
                sx={{
                  width: { md: 'auto', sm: '100%', xs: '100%' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff !important',
                    borderRadius: '5px',
                  },
                  '& .MuiNativeSelect-select': {
                    color: '#fff',
                    fontSize: '12px !important',
                  },
                }}
              >
                <option value="">{translate('all')}</option>
                <option value="deposit">{translate('deposit')}</option>
                <option value="withdraw">{translate('withdraw')}</option>
                <option value="bonus">{translate('bonus')}</option>
                <option value="rebate">{translate('rebate')}</option>
                <option value="adjustment">{translate('adjustment')}</option>
              </TextField>
              <InputStack onClick={() => setOpenDateRangePicker(true)} direction={'row'}>
                <InputText>{`${
                  filters?.dateFrom ? moment(filters?.dateFrom).format('DD/MM/YYYY') : translate('from')
                } - ${filters?.dateTo ? moment(filters?.dateTo).format('DD/MM/YYYY') : translate('to')}`}</InputText>
                <Iconify
                  icon={'material-symbols-light:calendar-today'}
                  sx={{ color: '#FCD509', width: 20, height: 20 }}
                />
              </InputStack>
              {/* <Stack
                direction="row"
                spacing={1}
                sx={{ color: '#fff', border: '2px solid #c6c6c6', borderRadius: '4px' }}
                alignItems={'center'}
              >
                <CustomTextField
                  value={filters?.dateFrom}
                  onChange={() => {}}
                  placeholder={translate('date_from')}
                  size="small"
                  onClick={() => setOpenDateRangePicker(true)}
                  sx={{ input: { color: 'white' } }}
                />
                <Stack alignItems={'center'}>-</Stack>
                <CustomTextField
                  value={filters?.dateTo}
                  onChange={() => {}}
                  placeholder={translate('date_to')}
                  size="small"
                  onClick={() => setOpenDateRangePicker(true)}
                  sx={{ input: { color: 'white' } }}
                />
              </Stack> */}
              <StyledButton
                variant="contained"
                onClick={() => {
                  setPage(1);
                  setFilters(_filters);
                }}
              >
                {translate('clear')}
              </StyledButton>
            </Stack>
          </Stack>
          <Scrollbar sx={{ p: { xs: 1 } }}>
            {tableData.length !== 0 ? (
              (isLoading ? [...Array(rowsPerPage)] : tableData).map((row, index) =>
                row ? (
                  <Stack key={row.id} mb={1}>
                    <StyledCard>
                      <StyledCardContent>
                        <Stack direction="row" spacing={1} justifyContent={'space-between'} alignItems={'center'}>
                          <Stack direction="row" spacing={1} alignItems={'center'}>
                            <Typography variant="body1">{row.title}</Typography>
                            <StyledCardTag status={row.transaction_status}>
                              <Typography variant="caption" sx={{ fontSize: '10px' }}>
                                {row.transaction_status.toUpperCase()}
                              </Typography>
                            </StyledCardTag>
                          </Stack>
                          <Typography variant="caption" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {fDate(row.date)}
                          </Typography>
                        </Stack>
                        <Stack direction="column">
                          <Typography variant="body2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {row.activity_type?.toUpperCase()}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          mt={1}
                          justifyContent={'space-between'}
                          alignItems={'center'}
                        >
                          <Stack direction="row" spacing={1} flex={1}>
                            <Stack direction="column" flex={1}>
                              <StyledCardSmallText variant="caption">{translate('c_amount')}</StyledCardSmallText>
                              <Typography variant="body2">
                                {process.env.REACT_APP_CURRENCY_CODE} {parseFloat(row.amount?.value)}
                              </Typography>
                            </Stack>
                            <Divider orientation="vertical" flexItem />
                            <Stack direction="column" flex={1}>
                              <StyledCardSmallText variant="caption">{translate('c_bonus')}</StyledCardSmallText>
                              <Typography variant="body2">
                                {process.env.REACT_APP_CURRENCY_CODE} {parseFloat(row.bonus?.value)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </StyledCardContent>
                    </StyledCard>
                    <StyledCardBottom>
                      <Stack direction="column">
                        <Typography variant="caption" style={{ fontWeight: 300 }}>
                          {translate('c_remarks')}
                        </Typography>
                        <Typography variant="caption" style={{ fontWeight: 300 }}>
                          {row.remark || '-'}
                        </Typography>
                      </Stack>
                    </StyledCardBottom>
                  </Stack>
                ) : (
                  !isNotFound && <TableSkeleton key={index} sx={{ height: denseHeight }} />
                )
              )
            ) : (
              <EmptyContainer direction={'column'} alignItems={'center'} py={5}>
                <SvgIconStyle
                  src={`/assets/icons/others/folder.svg`}
                  sx={{ height: 90, width: 90, color: 'rgba(255, 255, 255, 0.2)' }}
                />
                <Typography variant="body2" sx={{ padding: 0 }}>
                  Nothing to show
                </Typography>
              </EmptyContainer>
            )}
            {/* NEW PAGINATION START */}
            <Stack direction="row" spacing={1} my={2} justifyContent={'end'} alignItems={'center'}>
              <Stack direction="row" spacing={1} alignItems={'center'}>
                <StyledButton
                  variant="contained"
                  disabled={page === 1}
                  onClick={() => {
                    if (page > 1) {
                      setPage(page - 1);
                    }
                  }}
                  sx={{ minWidth: 0, paddingX: 1 }}
                >
                  <SvgIconStyle src={`/assets/icons/pagination/arrow_back.svg`} sx={{ height: 20 }} />
                </StyledButton>
                <Typography sx={{ paddingX: 2, paddingY: 0, margin: 0 }}>{page}</Typography>
                <StyledButton
                  variant="contained"
                  disabled={tableData.length < pageLimit}
                  onClick={() => {
                    setPage(page + 1);
                  }}
                  sx={{ minWidth: 0, paddingX: 1 }}
                >
                  <SvgIconStyle src={`/assets/icons/pagination/arrow_next.svg`} sx={{ height: 20 }} />
                </StyledButton>
              </Stack>
            </Stack>
          </Scrollbar>
        </Box>
      </Box>
      {openDateRangePicker && (
        <CustomDateRangePicker
          open={openDateRangePicker}
          setOpen={setOpenDateRangePicker}
          setDateRange={setDateRange}
          dateRange={dateRange}
        />
      )}
    </Page>
  );
};

export default TransactionHistory;
